import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Play, Loader2, ArrowLeft, Building2, RotateCw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageTitle, StatusBadge } from "@/components/StatusBadge";
import { PerceelMap } from "@/components/PerceelMap";
import { CompareViewer } from "@/components/CompareViewer";
import { AiAssessment } from "@/components/AiAssessment";
import { UploadsPanel } from "@/components/UploadsPanel";
import { VergunningenPanel } from "@/components/VergunningenPanel";

const Info = ({ k, v, mono }) => (
  <div className="flex justify-between gap-3 py-1.5 border-b border-slate-800/60 last:border-0 text-sm">
    <span className="text-slate-500">{k}</span><span className={`text-right ${mono ? "font-mono text-xs text-sky-300" : ""}`}>{v ?? "–"}</span>
  </div>
);

export default function AdresDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { data: a } = useQuery({ queryKey: ["address", id], queryFn: () => api.get(`/addresses/${id}`).then((r) => r.data) });
  const { data: s } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
  const { data: years = [] } = useQuery({ queryKey: ["years"], queryFn: () => api.get("/years").then((r) => r.data.years) });
  const [mapYear, setMapYear] = useState(null);
  if (!a || !s) return <div className="text-slate-500 font-mono text-xs">LADEN…</div>;
  const latest = a.analyses?.[0];

  const run = async (force) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/addresses/${id}/analyze`, null, { params: { force } });
      if (data.skipped) toast.info(data.reason);
      else toast.success(data.melding_id ? "Wijziging gedetecteerd — melding aangemaakt" : "Geen relevante wijziging gevonden");
      qc.invalidateQueries();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <Link to="/adressen" className="text-xs text-slate-400 hover:text-white flex items-center gap-1 mb-4" data-testid="back-to-addresses"><ArrowLeft className="h-3.5 w-3.5" /> Adressen</Link>
      <PageTitle eyebrow={`Perceel ${a.perceel?.aanduiding || "–"}`} title={a.weergavenaam}>
        <StatusBadge status={a.status} testId="address-status" />
        <button data-testid="analyze-address-btn" onClick={() => run(false)} disabled={busy} className="flex items-center gap-2 h-10 px-4 rounded-md bg-blue-600 hover:bg-blue-500 text-sm font-medium disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Analyseer {s.baseline_year} → {s.compare_year}
        </button>
        <button data-testid="reanalyze-address-btn" onClick={() => run(true)} disabled={busy} className="flex items-center gap-2 h-10 px-3 rounded-md border border-slate-700 hover:border-blue-500 text-sm">
          <RotateCw className="h-4 w-4" /> Opnieuw
        </button>
      </PageTitle>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <div className="border border-slate-800 bg-[#111827]/80 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-semibold">Kaart · perceel & BAG-panden</h3>
              <select data-testid="map-year-select" value={mapYear || s.compare_year} onChange={(e) => setMapYear(+e.target.value)} className="h-8 rounded bg-slate-950 border border-slate-800 px-2 text-xs font-mono">
                {[...years].reverse().map((y) => <option key={y} value={y}>Luchtfoto {y}</option>)}
              </select>
            </div>
            <div className="h-[420px]"><PerceelMap address={a} year={mapYear || s.compare_year} /></div>
            <div className="flex gap-4 mt-2 text-[11px] text-slate-500"><span className="text-sky-400">━ Perceelgrens</span><span className="text-amber-400">┅ BAG-pand</span></div>
          </div>
          {latest && (
            <div className="border border-slate-800 bg-[#111827]/80 rounded-lg p-4">
              <h3 className="font-display font-semibold mb-3">Vergelijking {latest.baseline_year} → {latest.upload_id ? latest.compare_label : latest.compare_year}</h3>
              <CompareViewer addressId={a.id} analysis={latest} highlight={(latest.ai?.objecten || []).filter((o) => o.is_bouwwerk).map((o) => o.regio)} />
            </div>
          )}
        </div>
        <div className="lg:col-span-4 space-y-6">
          <div className="border border-slate-800 bg-[#111827]/80 rounded-lg p-5" data-testid="address-info">
            <h3 className="font-display font-semibold mb-2">Gegevens</h3>
            <Info k="Postcode" v={a.postcode} mono />
            <Info k="Gemeente" v={a.gemeente} />
            <Info k="Perceel" v={a.perceel?.aanduiding} mono />
            <Info k="Perceel opp." v={a.perceel?.grootte_m2 && `${a.perceel.grootte_m2} m²`} mono />
            <Info k="BAG-ID" v={a.identificatie} mono />
            <Info k="Basisjaar" v={a.baseline_year} mono />
          </div>
          <div className="border border-slate-800 bg-[#111827]/80 rounded-lg p-5">
            <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><Building2 className="h-4 w-4" /> BAG-panden ({a.panden?.length || 0})</h3>
            {(a.panden || []).map((p) => (
              <div key={p.identificatie} className="text-xs py-1.5 border-b border-slate-800/60 last:border-0">
                <div className="font-mono text-sky-300">{p.identificatie}</div>
                <div className="text-slate-400">Bouwjaar {p.bouwjaar} · {p.oppervlakte_m2} m² · {p.status}</div>
              </div>
            ))}
          </div>
          <VergunningenPanel addressId={a.id} />
          <UploadsPanel address={a} baselineYear={s.baseline_year} />
          {latest && (
            <div className="border border-slate-800 bg-[#111827]/80 rounded-lg p-5">
              {latest.ai ? <AiAssessment {...latest.ai} aiError={latest.ai_error} /> :
                <p className="text-sm text-slate-400">{latest.regions?.length ? `Pixelverschil ${latest.changed_area_m2} m² — ${latest.ai_error ? "AI niet beschikbaar" : ""}` : "Geen significante pixelverschillen op dit perceel."}</p>}
              <div className="mt-4 flex flex-wrap gap-2">
                {(a.meldingen || []).map((m) => (
                  <Link key={m.id} to={`/meldingen/${m.id}`} className="text-xs text-blue-400 hover:underline" data-testid={`address-melding-link-${m.id}`}>Melding {m.baseline_year}→{m.upload_id ? m.compare_label : m.compare_year} ({m.status}) →</Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
